import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faArrowLeft,
  faArrowRight,
  faKey,
  faExclamationCircle,
  faInbox,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import AuthLangSwitcher from '../components/AuthLangSwitcher';
import { openMailClient } from '../utils/mailSupport';
import '../styles/AuthModern.css';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError(t('auth.emailRequired2'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Success state — email sent
  if (sent) {
    return (
      <div className="auth-page">
        <div className="auth-status-layout">
          <div className="auth-status-card">
            <AuthLangSwitcher />
            <div className="auth-status-icon success">
              <FontAwesomeIcon icon={faInbox} />
            </div>
            <h2 className="auth-status-title">{t('auth.checkInbox')}</h2>
            <p className="auth-status-desc">{t('auth.resetEmailSentDesc')}</p>
            <div className="auth-status-actions">
              <button
                className="auth-submit-btn"
                onClick={() => navigate('/login')}
                style={{ maxWidth: 320 }}
              >
                {t('auth.backToLogin')}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        {/* Left Branding Panel */}
        <div className="auth-brand-panel">
          <div className="auth-brand-logo">
            <div className="auth-brand-logo-icon">E</div>
            <div className="auth-brand-logo-text">ELVOAQ</div>
          </div>

          <h2 className="auth-brand-heading">
            {t('auth.resetYourPassword').split(' ').slice(0, 1).join(' ')}{' '}
            <span>{t('auth.resetYourPassword').split(' ').slice(1).join(' ')}</span>
          </h2>
          <p className="auth-brand-desc">{t('auth.resetDescription')}</p>

          <div className="auth-features-list">
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faKey} />
              </div>
              <span className="auth-feature-text">{t('auth.secureLogin')}</span>
            </div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-card">
            <AuthLangSwitcher />

            <button className="auth-back-btn" onClick={() => navigate('/login')} type="button">
              <FontAwesomeIcon icon={faArrowLeft} />
              {t('auth.backToLogin')}
            </button>

            <div className="auth-form-header">
              <h1>{t('auth.forgotPasswordTitle')}</h1>
              <p>{t('auth.forgotPasswordSubtitle')}</p>
            </div>

            {error && (
              <div className="auth-error">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faEnvelope} />
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  className="auth-input"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder={t('auth.emailPlaceholder')}
                  disabled={loading}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="auth-spinner" />
                    {t('auth.sending')}
                  </>
                ) : (
                  <>
                    {t('auth.sendResetLink')}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>
            </form>

            <div className="auth-footer-link">
              {t('auth.hasAccount')}{' '}
              <button type="button" onClick={() => navigate('/login')}>
                {t('auth.signInLink')}
              </button>
            </div>

            <div className="auth-support-section">
              <p className="auth-support-text">{t('auth.havingProblems')}</p>
              <button
                type="button"
                className="auth-support-btn"
                onClick={() => openMailClient(t('auth.supportSubject'), t('auth.supportBody'))}
              >
                <FontAwesomeIcon icon={faHeadset} />
                {t('auth.reachOutToUs')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
